
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import {
  ILeadProviderData,
  ILeadProviderMethods
} from "@/models/providers/lead";

// Data
import { ConfirmModalModes, UUID } from "@/data/enums";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import AdminBrandId from "@/mixins/adminBrandId";
import { IProvider } from "@/models/providers";
import { ILead } from "@/models/leads";
import _ from "@/boot/lodash";
import { IClient } from "@/models/clients";

@Component({
  computed: {
    ...mapState({
      lead(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.LEADS,
          scope: this.leadScope
        });
      },
      leadScope(state, getters) {
        return getters[`data/${DataModules.LEADS}/scope`](this.leadId);
      }
    })
  }
})
export default class LeadProvider extends Mixins(RequestPromises, AdminBrandId)
  implements IProvider {
  // Props

  @Prop({ required: true })
  public readonly leadId!: ILead["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: LeadProvider) => {
      return vm.$router.replace({ name: "adminLeads" }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public readonly lead!: ILead;
  public readonly leadScope!: string;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = {};
  public hasRequestError = false;

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading && !this.isReloading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $leadData: this.providerData(),
        $leadMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    // Bin client data
    this.$store.dispatch("data/binSingle", {
      scope: this.leadScope,
      storeModule: DataModules.LEADS,
      vm: this
    });
  }

  // Computed

  public get isStaged() {
    return !!_.get(this.lead, "staged_import");
  }

  public get isImported() {
    return !this.isStaged && !!_.get(this.lead, "import_id");
  }

  public get canDelete(): boolean {
    return this.$userCan("delete_lead");
  }

  public get canConvertToClient(): boolean {
    return this.$userCan("create_client");
  }

  public get canMergeWithClient(): boolean {
    return this.$userCan("import_lead_to_client");
  }

  public get canUpdateLead(): boolean {
    return this.$userCan("update_lead");
  }

  public get canDeleteLead(): boolean {
    return this.$userCan("delete_lead");
  }

  public get canAttach() {
    return this.$userCan("get_lead");
  }

  public get canAttachMain() {
    return this.$userCan("get_lead");
  }

  public get canDetach() {
    return this.$userCan("get_lead");
  }

  public get canChangeMain() {
    return this.$userCan("assign_main_lead");
  }

  public get canCreateTicket() {
    return this.$userCan("create_ticket");
  }

  public get mainLead() {
    if (this.lead.is_main) return this.lead;
    return this.$_.find(this.lead.related_leads, ["is_main", true]) as ILead;
  }

  // Methods

  public loadData({ ignoreStored } = { ignoreStored: false }) {
    return this.getLead(ignoreStored);
  }

  public async getLead(ignoreStored = false): Promise<any> {
    try {
      const { brand_id } = await this.$store.dispatch(
        `data/${DataModules.LEADS}/get`,
        {
          id: this.leadId,
          ignoreStored,
          params: {
            with_staged_imports: 1,
            with: "related_leads,brand,tags,image"
          },
          scope: this.leadScope,
          vm: this
        }
      );

      if (
        brand_id !== this.brandId &&
        this.brandId !== UUID.ORG &&
        this.onNotFound
      )
        this.onNotFound(this);
      this.hasRequestError = false;
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.getLead(true);
  }

  public async updateLead(data) {
    await this.$store.dispatch(`data/${DataModules.LEADS}/update`, {
      id: this.lead.id,
      scope: this.leadScope,
      data
    });
    this.$buefy.toast.open({
      message: this.$t("_.lead_updated") as string
    });
  }

  public async confirmDelete(lead: ILead) {
    if (!this.canDeleteLead) return;
    lead = lead || this.lead;
    let message = "delete_confirmation";
    if (lead?.related_leads?.length) {
      message = "delete_lead_with_relations";
    }
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$tc(
            `_sentence.${message}`,
            lead?.related_leads?.length,
            {
              object: (this.$t("_.lead") as string).toLowerCase()
            }
          )
        },
        events: {
          confirmed: async () => {
            await this.deleteLead(lead);
            confirm.close();
          }
        }
      }
    });
  }

  public async deleteLead(lead: ILead) {
    if (!this.$userCan("delete_lead")) return;
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.LEADS}/remove`, {
        id: lead.id
      });
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_removed", {
          object: this.$t("_.lead")
        }) as string
      });
      if (this.lead.id === lead.id)
        return this.$router.push({ name: "adminLeadsList" });
      this.reloadData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public convertToClient() {
    if (!this.canConvertToClient) return;
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS}/openAddClientModal`,
      {
        props: {
          lead: this.lead
        }
      }
    );
  }

  public async mergeWithExistingClient() {
    if (!this.canMergeWithClient) return;
    try {
      const modal = await this.$store.dispatch(
        `data/${DataModules.CLIENTS}/openSelectClientModal`,
        {
          props: {
            brandId: this.lead.brand_id
          },
          events: {
            onSelect: async client => {
              modal.close();
              const confirm = await this.$store.dispatch(
                "ui/open/confirmModal",
                {
                  config: {
                    props: {
                      autoClose: false,
                      mode: ConfirmModalModes.BUTTON,
                      confirmButtonText: this.$t("_action.merge"),
                      message: this.$t("_sentence.merge_lead_confirm", {
                        lead: this.lead.name || this.lead.email,
                        client: `${client.firstname} ${client.lastname}`
                      })
                    },
                    events: {
                      confirmed: async () => {
                        await this.mergeConfirmed(client);
                        confirm.close();
                      }
                    }
                  }
                }
              );
            }
          }
        }
      );
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public openNewTicketModal() {
    if (!this.canCreateTicket) return;
    this.$store.dispatch("data/tickets/openNewTicketModal", {
      leadId: this.lead?.id,
      brandId: this.lead?.brand_id
    });
  }

  public async mergeConfirmed(client: IClient) {
    try {
      await this.$store.dispatch(`data/${DataModules.CLIENTS}/mergeLead`, {
        id: client.id,
        leadId: this.lead.id
      });

      this.$buefy.toast.open({
        message: this.$t(
          "_sentence.lead_successfully_merged_with_client"
        ) as string
      });
      return this.$router.replace({
        name: "adminClientOverview",
        params: { clientId: client.id }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async updateHidden() {
    if (!this.canUpdateLead) return;
    this.isProcessing = true;
    try {
      const isHidden = !this.lead.is_hidden;
      await this.$store.dispatch(`data/${DataModules.LEADS}/update`, {
        id: this.lead.id,
        data: { is_hidden: isHidden }
      });
      if (isHidden) {
        this.$router.push({ name: "adminLeadsList" });
      } else {
        await this.reloadData();
      }
      this.$buefy.toast.open({
        message: this.$t(
          isHidden
            ? "_.lead_hidden_successfully"
            : "_.lead_unhidden_successfully"
        ) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.isProcessing = false;
  }

  public async selectLead() {
    const modal = await this.$store.dispatch(
      `data/${DataModules.LEADS}/openSelectLeadModal`,
      {
        props: {
          excludedLeadsIds: [
            this.lead.id,
            ...this.lead.related_leads.map(lead => lead.id)
          ]
        },
        events: {
          "lead-selected": async (lead: ILead) => {
            modal.close();
            try {
              let attached = false;
              if (lead.related_leads.length) {
                if (!lead.is_main) {
                  lead = this.$_.find(lead.related_leads, [
                    "is_main",
                    true
                  ]) as ILead;
                }

                if (this.lead.related_leads) {
                  await this.attachMain(lead);
                  attached = true;
                }
              }
              if (!attached) {
                await this.attach(lead);
              }
              this.reloadData();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
          }
        }
      }
    );
  }

  public attach(lead: ILead) {
    if (!this.canAttach) return Promise.resolve();
    return this.$store.dispatch(`data/${DataModules.LEADS}/attach`, {
      id: this.mainLead.id,
      relatedId: lead.id,
      data: {
        brand_id: this.mainLead.brand_id
      }
    });
  }

  public attachMain(lead: ILead) {
    if (!this.canAttachMain) return Promise.resolve();
    return this.$store.dispatch(`data/${DataModules.LEADS}/attachMain`, {
      id: this.mainLead.id,
      relatedId: lead.id,
      data: {
        brand_id: this.mainLead.brand_id
      }
    });
  }

  public async confirmDetach(lead: ILead) {
    if (!this.canDetach) return Promise.resolve();
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          mode: ConfirmModalModes.BUTTON,
          autoClose: false,
          confirmButtonText: this.$t("_action.unlink"),
          message: this.$t("_sentence.unlink_confirmation", {
            object: (this.$t("_.relation") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            await this.detach(lead);
            this.reloadData();
            confirm.close();
          }
        }
      }
    });
  }

  public detach(lead: ILead) {
    if (!this.canDetach) return Promise.resolve();
    return this.$store.dispatch(`data/${DataModules.LEADS}/detach`, {
      id: this.mainLead.id,
      relatedId: lead.id === this.mainLead.id ? this.lead.id : lead.id,
      data: {
        brand_id: lead.brand_id
      }
    });
  }

  public async changeMain(lead: ILead) {
    if (!this.canChangeMain) return Promise.resolve();
    try {
      await this.$store.dispatch(`data/${DataModules.LEADS}/changeMain`, {
        id: lead.id,
        data: {
          brand_id: lead.brand_id
        }
      });

      return this.$router.replace({
        name: "adminLeadOverview",
        params: { leadId: lead.id }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  // Provide

  @Provide("leadProvider")
  public providerData(): ILeadProviderData {
    return {
      canAttach: this.canAttach,
      canAttachMain: this.canAttachMain,
      canChangeMain: this.canChangeMain,
      canConvertToClient: this.canConvertToClient,
      canCreateTicket: this.canCreateTicket,
      canDeleteLead: this.canDeleteLead,
      canDetach: this.canDetach,
      canDelete: this.canDelete,
      canMergeWithClient: this.canMergeWithClient,
      canUpdateLead: this.canUpdateLead,
      hasRequestError: this.hasRequestError,
      isImported: this.isImported,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      isStaged: this.isStaged,
      lead: this.lead,
      leadId: this.leadId,
      scope: this.leadScope
    };
  }

  @Provide("leadMethods")
  public get providerMethods(): ILeadProviderMethods {
    return {
      attach: this.attach,
      attachMain: this.attachMain,
      changeMain: this.changeMain,
      confirmDelete: this.confirmDelete,
      confirmDetach: this.confirmDetach,
      convertToClient: this.convertToClient,
      detach: this.detach,
      mergeWithExistingClient: this.mergeWithExistingClient,
      openNewTicketModal: this.openNewTicketModal,
      reloadData: this.reloadData,
      selectLead: this.selectLead,
      updateHidden: this.updateHidden,
      updateLead: this.updateLead
    };
  }
}
